import React from 'react';
import {
  Edit,
  Loader2,
  UserPlus,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useAdminContext } from '../../../contexts/AdminContext';

const UserModals = () => {
  const {
    charAt,
    creatingUser,
    editUserData,
    handleCreateUser,
    handleEditUser,
    handleSaveUserEdit,
    loadingUserDetail,
    newUserData,
    selectedUser,
    setEditUserData,
    setNewUserData,
    setShowCreateUserModal,
    setShowEditUserModal,
    setShowUserDetailModal,
    showCreateUserModal,
    showEditUserModal,
    showUserDetailModal,
    userDetailStats,
  } = useAdminContext();

  return (
    <>
    {/* User Detail Modal */}
    <Dialog open={showUserDetailModal} onOpenChange={setShowUserDetailModal}>
      <DialogContent className="max-w-2xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {selectedUser?.picture ? (
              <img src={selectedUser.picture} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-slate-900 dark:text-white text-xl font-bold">
                {selectedUser?.name?.charAt(0) || 'U'}
              </div>
            )}
            <div>
              <div className="text-xl">{selectedUser?.name || 'Gebruiker'}</div>
              <div className="text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400">{selectedUser?.email}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loadingUserDetail ? (
          <div className="py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
          </div>
        ) : userDetailStats ? (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg p-3 border border-blue-500/30">
                <div className="text-xs text-blue-300 mb-1">Logins</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">{userDetailStats.stats?.login_count || 0}</div>
                <div className="text-xs text-blue-400">{userDetailStats.stats?.login_frequency_per_week || 0}x/week</div>
              </div>
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-lg p-3 border border-green-500/30">
                <div className="text-xs text-green-300 mb-1">Quickscans</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">{userDetailStats.stats?.completed_assessments || 0}</div>
                <div className="text-xs text-green-400">Gem: {userDetailStats.stats?.average_score || 0}%</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg p-3 border border-blue-500/30">
                <div className="text-xs text-blue-300 mb-1">Use Cases</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">{userDetailStats.stats?.saved_use_cases || 0}</div>
                <div className="text-xs text-blue-400">opgeslagen</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-lg p-3 border border-purple-500/30">
                <div className="text-xs text-purple-300 mb-1">Dagen Actief</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white">{userDetailStats.stats?.days_active || 0}</div>
                <div className="text-xs text-purple-400">dagen</div>
              </div>
            </div>

            {/* User Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400">Account Info</div>
                <div className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400">Login Methode</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${userDetailStats.auth_method === 'google' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {userDetailStats.auth_method === 'google' ? 'Google' : 'Email'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400">Beta Tester</span>
                    <span className={userDetailStats.is_beta_tester ? 'text-green-400' : 'text-slate-500 dark:text-slate-500'}>{userDetailStats.is_beta_tester ? 'Ja' : 'Nee'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400">ROI Toegang</span>
                    <span className={userDetailStats.roi_enabled ? 'text-green-400' : 'text-slate-500 dark:text-slate-500'}>{userDetailStats.roi_enabled ? 'Ja' : 'Nee'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400">Geregistreerd</span>
                    <span className="text-slate-900 dark:text-white">{userDetailStats.stats?.created_at ? new Date(userDetailStats.stats.created_at).toLocaleDateString('nl-NL') : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-500 dark:text-slate-400">Laatste Login</span>
                    <span className="text-slate-900 dark:text-white">{userDetailStats.stats?.last_login_at ? new Date(userDetailStats.stats.last_login_at).toLocaleString('nl-NL') : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Activity by Day Chart */}
              <div className="space-y-2">
                <div className="text-sm text-slate-500 dark:text-slate-500 dark:text-slate-400">Activiteit per Dag</div>
                <div className="bg-slate-100 dark:bg-slate-700/50 rounded-lg p-3 h-32">
                  {userDetailStats.activity_by_day && (
                    <div className="flex items-end justify-between h-full gap-1">
                      {userDetailStats.activity_by_day.map((item, idx) => (
                        <div key={item.day || `day-${idx}`} className="flex flex-col items-center flex-1">
                          <div 
                            className="w-full bg-blue-500 rounded-t"
                            style={{ 
                              height: `${Math.max(4, (item.logins / Math.max(...userDetailStats.activity_by_day.map(d => d.logins), 1)) * 80)}px`,
                              minHeight: item.logins > 0 ? '8px' : '4px'
                            }}
                          />
                          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">{item.day}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Login Methods */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-slate-700 dark:text-slate-300">Email: {userDetailStats.login_methods?.email || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-slate-700 dark:text-slate-300">Google: {userDetailStats.login_methods?.google || 0}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUserDetailModal(false);
                  handleEditUser(selectedUser);
                }}
                className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
              >
                <Edit className="h-4 w-4 mr-2" />
                Bewerken
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowUserDetailModal(false)}
                className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
              >
                Sluiten
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Kon gebruiker gegevens niet laden
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Edit User Modal */}
    <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
      <DialogContent className="max-w-md bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-yellow-400" />
            Gebruiker Bewerken
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Naam</label>
            <Input
              value={editUserData.name}
              onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="Gebruikersnaam"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Nieuw Wachtwoord <span className="text-slate-500 dark:text-slate-500">(optioneel)</span>
            </label>
            <Input
              type="password"
              value={editUserData.newPassword}
              onChange={(e) => setEditUserData({ ...editUserData, newPassword: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="Laat leeg om niet te wijzigen"
            />
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">Minimaal 6 karakters. Dit zal ook Google login overschrijven naar email login.</p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={() => setShowEditUserModal(false)}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleSaveUserEdit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              Opslaan
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Create User Modal */}
    <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
      <DialogContent className="max-w-md bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-green-400" />
            Nieuwe Gebruiker Aanmaken
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Naam <span className="text-red-400">*</span>
            </label>
            <Input
              type="text"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="Volledige naam"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              E-mail <span className="text-red-400">*</span>
            </label>
            <Input
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="email@voorbeeld.nl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Wachtwoord <span className="text-red-400">*</span>
            </label>
            <Input
              type="password"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="Minimaal 6 karakters"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Rol
            </label>
            <select
              value={newUserData.role}
              onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
              className="w-full bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded-md px-3 py-2"
            >
              <option value="user">Gebruiker</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateUserModal(false);
                setNewUserData({ email: '', name: '', password: '', role: 'user' });
              }}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creatingUser || !newUserData.email || !newUserData.name || !newUserData.password}
              className="bg-green-600 hover:bg-green-700"
            >
              {creatingUser ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aanmaken...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Aanmaken
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default UserModals;
